import { useState } from "react";
import { ClipboardPenLine, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const emptyEntry = { date: "", minutes: 30, contact_type: "Direct observation", client_id: "", client_initials: [], caseload_reviewed: false, covered: "", direction_given: "", follow_up_due: "" };

export function SupervisionEntryForm({ pairing, overview, role, onSaved }) {
  const [entry, setEntry] = useState({ ...emptyEntry, client_id: pairing.sites?.[0]?.id || "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const openMonths = overview.open_months || [];
  const minDate = openMonths.length ? `${openMonths[0].month}-01` : "";
  const update = (field) => (event) => setEntry({ ...entry, [field]: event.target.type === "checkbox" ? event.target.checked : event.target.value });
  const toggleInitials = (initials) => setEntry({ ...entry, client_initials: entry.client_initials.includes(initials) ? entry.client_initials.filter((i) => i !== initials) : [...entry.client_initials, initials] });
  const other = role === "supervisor" ? pairing.assistant_name : pairing.supervisor_name;
  const isDirect = entry.contact_type === "Direct observation";

  async function submit(event) {
    event.preventDefault();
    try {
      await api.post("/supervision/log", { ...entry, minutes: Number(entry.minutes), pair_id: pairing.id });
      setEntry({ ...emptyEntry, client_id: entry.client_id });
      setError("");
      setNotice(`Entry saved. It counts once ${other} confirms it from their own login.`);
      await onSaved();
    } catch (requestError) {
      setNotice("");
      setError(formatApiError(requestError));
    }
  }

  if (!openMonths.length) return <p className="pairing-meta" data-testid={`logging-closed-${pairing.id}`}>Logging is closed for the current period.</p>;
  return (
    <form className="supervision-note-form supervision-log-form" data-testid={`supervision-log-form-${pairing.id}`} onSubmit={submit}>
      <p className="caseload-guidance provider-report-wide" data-testid={`logging-open-${pairing.id}`}>
        <ShieldCheck size={13} /> Logging open for {openMonths.map((m) => m.label).join(" and ")} — enter every session, including ones already completed. Due {openMonths[0].deadline_label}; released supervision time is paid {openMonths[0].pay_date_label}. Student initials only, never full names.
      </p>
      <label>Session date<input data-testid={`log-date-${pairing.id}`} max={overview.today} min={minDate} required type="date" value={entry.date} onChange={update("date")} /></label>
      <label>Minutes<input data-testid={`log-minutes-${pairing.id}`} max="600" min="5" required step="5" type="number" value={entry.minutes} onChange={update("minutes")} /></label>
      <label>Type of contact<select data-testid={`log-type-${pairing.id}`} value={entry.contact_type} onChange={update("contact_type")}>{overview.contact_types.map((t) => <option key={t}>{t}</option>)}</select></label>
      <label>School<select data-testid={`log-site-${pairing.id}`} required value={entry.client_id} onChange={update("client_id")}><option value="">Select the school…</option>{(pairing.sites || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
      <fieldset className="provider-report-wide initials-picker" data-testid={`log-initials-${pairing.id}`}>
        <legend>{isDirect ? "Student initials observed (required)" : "Student initials or caseload reviewed"}</legend>
        {pairing.caseload.map((c) => <label key={c.initials}><input checked={entry.client_initials.includes(c.initials)} data-testid={`log-initial-${pairing.id}-${c.initials}`} type="checkbox" onChange={() => toggleInitials(c.initials)} /> {c.initials}</label>)}
        {!isDirect && <label><input checked={entry.caseload_reviewed} data-testid={`log-caseload-reviewed-${pairing.id}`} type="checkbox" onChange={update("caseload_reviewed")} /> Whole caseload reviewed</label>}
        {!pairing.caseload.length && <span className="supervision-totals-empty">Add students to the caseload first (initials only).</span>}
      </fieldset>
      <label className="provider-report-wide">What was covered<textarea data-testid={`log-covered-${pairing.id}`} required rows="2" value={entry.covered} onChange={update("covered")} /></label>
      {role === "supervisor" && <label className="provider-report-wide">Direction given (optional)<textarea data-testid={`log-direction-${pairing.id}`} rows="2" value={entry.direction_given} onChange={update("direction_given")} /></label>}
      <label>Follow-up due (optional)<input data-testid={`log-followup-${pairing.id}`} type="date" value={entry.follow_up_due} onChange={update("follow_up_due")} /></label>
      {error && <p className="form-message error-message provider-report-wide" data-testid={`log-error-${pairing.id}`}>{error}</p>}
      {notice && <p className="form-message success-message provider-report-wide" data-testid={`log-notice-${pairing.id}`}>{notice}</p>}
      <button className="button button-primary" data-testid={`submit-log-${pairing.id}`}><ClipboardPenLine size={15} /> Save entry</button>
    </form>
  );
}

const ENTRY_STATUS_CLASS = { Confirmed: "status-active", "Awaiting confirmation": "status-pending", Disputed: "status-inactive" };

export function EntryList({ pairing, role }) {
  const entries = pairing.entries || [];
  if (!entries.length) return <p className="pairing-meta" data-testid={`entries-empty-${pairing.id}`}>No supervision logged yet.</p>;
  return (
    <details className="supervision-history" data-testid={`entries-${pairing.id}`} open>
      <summary>Logged sessions ({entries.length})</summary>
      <table className="supervision-totals" data-testid={`entries-table-${pairing.id}`}>
        <thead><tr><th>Date</th><th>Minutes</th><th>Type</th><th>School</th><th>Students</th><th>Entered by</th><th>Status</th></tr></thead>
        <tbody>
          {entries.map((item) => (
            <tr key={item.id} data-testid={`entry-row-${item.id}`}>
              <td>{item.note_date}</td>
              <td>{item.minutes}m</td>
              <td>{item.contact_type}</td>
              <td>{item.site_name || "—"}</td>
              <td>{item.caseload_reviewed ? "Caseload reviewed" : (item.client_initials || []).join(", ") || "—"}</td>
              <td>{item.entered_by_role === role ? "You" : item.entered_by_name}</td>
              <td><span className={`status-chip ${ENTRY_STATUS_CLASS[item.status] || ""}`} data-testid={`entry-status-${item.id}`}>{item.status}</span>{item.payable ? <><br /><small className="pairing-meta">Released · payable {item.pay_date}</small></> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}
